import { useState, useCallback, useEffect, useContext } from 'react';
import { badgeService } from '../services/badgeService';
import { UserContext } from '../contexts/UserContext';
import { useBadges } from '../contexts/BadgeContext';
import {
  transformStudentBadgeProgressFromAPI,
  transformStudentProgressFromAPI,
  transformXPHistoryFromAPI
} from '../utils/badgeTransformers';
import type { Activity, Badge, StudentBadgeProgress } from '../types/badges';

export const useStudentBadgeData = () => {
  const { userProfile } = useContext(UserContext);
  const studentId = userProfile?.userId ?? '';
  const { badges: badgeDefs, fetchBadges } = useBadges();

  const [badgeProgress, setBadgeProgress] = useState<StudentBadgeProgress[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const defs = badgeDefs.length ? badgeDefs : await fetchBadges();
      void defs; // defs kept for future use
      const progressAPI = await badgeService.getStudentBadgeProgress(studentId);
      setBadgeProgress(progressAPI.map(transformStudentProgressFromAPI));
      setBadges(progressAPI.map(transformStudentBadgeProgressFromAPI));
      const historyAPI = await badgeService.getStudentXPHistory(studentId);
      setActivities(transformXPHistoryFromAPI(historyAPI));
    } finally {
      setLoading(false);
    }
  }, [studentId, badgeDefs, fetchBadges]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const studentXP = badgeProgress.reduce((sum, p) => sum + p.currentXP, 0);

  // expose studentBadges as an explicit property (alias for badgeProgress)
  const studentBadges = badgeProgress;

  return { badgeProgress, studentBadges, badges, activities, studentXP, loading, refresh };
};
